import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useInsurance } from "@/hooks/useInsurance";
import FormHeader from "@/components/ui/organisms/MHeader";
import { MButton } from "@/components/ui/atoms/MButtons";
import { IconFileOff } from "@tabler/icons-react";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import { getMemberInfo } from "@/hooks/useMembers";
import MSpacer from "@/components/ui/atoms/MSpacer";
import MEmptyCard from "@/components/ui/organisms/MEmptyCard";
import MLoader from "@/components/ui/atoms/MLoader";
import { useTranslation } from "react-i18next";
import MInfoCard from "@/components/ui/atoms/MInfoCard";
import { formatPrettyDate } from "@/utils/date-utils";

const LinkFamilyPolicy: React.FC = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const memberId = searchParams.get("memberId");
  const memberInfo = getMemberInfo();

  const {
    familyInsurances,
    loading,
    error,
    fetchFamilyInsurances,
    linkFamilyMember,
  } = useInsurance({ memberInfo });
  const [selectedInsurances, setSelectedInsurances] = useState<
    Record<string, boolean>
  >({});
  const [isSaving, setIsSaving] = useState(false);

  // Fetch family insurances on mount and when memberId changes
  useEffect(() => {
    const fetchData = async () => {
      if (!memberId) {
        navigate(-1); // Go back if no memberId
        return;
      }

      try {
        // Pass the memberId to exclude insurances where this member is already covered
        await fetchFamilyInsurances(undefined, memberId);
      } catch (error) {
        console.error("Error in LinkFamilyPolicy:", error);
        showToast({
          message: error?.message || "Failed to fetch family insurances",
          variant: "error",
        });
      }
    };

    fetchData();

    // No cleanup needed for this effect
  }, [memberId, fetchFamilyInsurances, navigate]);

  // Filter out pet insurances for family linking (memberType !== 'p')
  const availableInsurances = familyInsurances.filter(
    (ins: any) => ins.memberType !== "p"
  );

  // Show loader while loading
  if (loading) {
    return <MLoader />;
  }

  // Handle checkbox toggle
  const handleToggleInsurance = (insuranceId: string) => {
    setSelectedInsurances((prev) => ({
      ...prev,
      [insuranceId]: !prev[insuranceId],
    }));
  };

  // Handle save button click
  const handleSave = async () => {
    if (!memberId) return;

    setIsSaving(true);
    try {
      const selectedIds = Object.entries(selectedInsurances)
        .filter(([_, isSelected]) => isSelected)
        .map(([id]) => id);

      // Link each selected insurance to the family member
      await Promise.all(
        selectedIds.map((insuranceId) =>
          linkFamilyMember(insuranceId, memberId)
        )
      );

      showToast({
        message: "Insurance policies linked successfully",
        variant: "success",
      });

      // Navigate to insurance list
      navigate("/profile/basic-information/insurance");
    } catch (error: any) {
      showToast({
        message: error?.message || "Failed to link insurance policies",
        variant: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="profile-page-root">
      <FormHeader module="link_insurance" memberInfo={memberInfo} />
      {!loading && availableInsurances.length === 0 ? (
        <>
          <MSpacer mt={24} />
          <MEmptyCard
            icon={<IconFileOff />}
            primaryTitle={t("link_insurance.noInsurance")}
            secondaryTitle={t("link_insurance.noInsuranceDescription")}
          />
        </>
      ) : !loading && availableInsurances.length > 0 ? (
        <>
          <MSpacer mt={24} />
          <div className="profile-input-container">
            {availableInsurances.map((insurance) => {
              const checked =
                !!selectedInsurances[insurance._id || insurance.id || ""];
              return (
                <div className="w-full" key={insurance._id || insurance.id}>
                  <MInfoCard
                    variant="checkbox"
                    primaryLabel={insurance.insurerName}
                    secondaryLabel={t("insurance.primaryHolder", {
                      name:
                        insurance.primaryPolicyHolderName ||
                        insurance.primaryPolicyHolder,
                    })}
                    tertiaryLabel={t("insurance.validUntil", {
                      date: formatPrettyDate(
                        insurance.policyEndDate || insurance.validUntil
                      ),
                    })}
                    checked={checked}
                    centeredRightAction={true}
                    onClick={() =>
                      handleToggleInsurance(insurance._id || insurance.id || "")
                    }
                  />
                </div>
              );
            })}
          </div>
          <div className="confirm-footer">
            <MButton
              type="button"
              variant="filled"
              size="lg"
              className="w-full"
              onClick={handleSave}
              disabled={
                isSaving || Object.values(selectedInsurances).every((v) => !v)
              }
              text={isSaving ? t("common.saving") : t("common.done")}
            />
          </div>
        </>
      ) : null}
    </div>
  );
};

export { LinkFamilyPolicy as default };
